import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Database } from 'lucide-react';
import { EditableCardActions } from './EditableCardActions';
import { updateSchool, updateSchoolModules } from '@/api/schools';
import type { SchoolEditData, SchoolOptions, UpdateSchoolData } from '@/types/school';

interface V1DeploymentCardProps {
  school: SchoolEditData;
  options: SchoolOptions;
  onSaved: () => void;
}

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function V1DeploymentCard({ school, options, onSaved }: V1DeploymentCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    status: school.status || undefined as UpdateSchoolData['status'],
    type: school.type,
    support: school.support,
    vip: school.vip || false,
    maintenance: school.maintenance || false,
    overdueAccount: school.overdueAccount || false,
    overdueAccountDate: school.overdueAccountDate,
    productionDate: school.productionDate,
  });
  const [moduleIds, setModuleIds] = useState<number[]>(school.moduleIds || []);

  const startEditing = useCallback(() => {
    setFormData({
      status: school.status || undefined,
      type: school.type,
      support: school.support,
      vip: school.vip || false,
      maintenance: school.maintenance || false,
      overdueAccount: school.overdueAccount || false,
      overdueAccountDate: school.overdueAccountDate,
      productionDate: school.productionDate,
    });
    setModuleIds(school.moduleIds || []);
    setError(null);
    setIsEditing(true);
  }, [school]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError(null);
  }, []);

  const handleChange = <K extends keyof typeof formData>(key: K, value: (typeof formData)[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleModuleToggle = (moduleId: number) => {
    setModuleIds((prev) =>
      prev.includes(moduleId) ? prev.filter((id) => id !== moduleId) : [...prev, moduleId]
    );
  };

  const save = useCallback(async () => {
    try {
      setIsSaving(true);
      setError(null);
      const payload: UpdateSchoolData = {
        status: formData.status,
        type: formData.type,
        support: formData.support,
        vip: formData.vip,
        maintenance: formData.maintenance,
        overdueAccount: formData.overdueAccount,
        overdueAccountDate: formData.overdueAccountDate,
        productionDate: formData.productionDate,
      };
      await updateSchool(school.code, payload);
      await updateSchoolModules(school.code, moduleIds);
      setIsEditing(false);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  }, [formData, moduleIds, school.code, onSaved]);

  const getModuleNames = () => {
    return school.moduleIds
      .map((id) => options.modules.find((m) => m.id === id)?.name)
      .filter(Boolean) as string[];
  };

  if (!isEditing) {
    return (
      <Card>
        <CardHeader
          icon={<Database />}
          iconColor="purple"
          actions={
            <EditableCardActions
              isEditing={false}
              isSaving={false}
              onEdit={startEditing}
              onSave={save}
              onCancel={cancelEditing}
            />
          }
        >
          <CardTitle>Offre V1</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Creation</p>
              <p>{formatDate(school.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Mise en production</p>
              <p>{formatDate(school.productionDate)}</p>
            </div>
          </div>

          <div className="pt-4 border-t">
            <p className="text-sm font-medium text-muted-foreground mb-2">
              Modules ({school.moduleIds.length})
            </p>
            {school.moduleIds.length === 0 ? (
              <p className="text-sm text-muted-foreground">Aucun module active</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {getModuleNames().map((name) => (
                  <Badge key={name} variant="secondary">
                    {name}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        icon={<Database />}
        iconColor="purple"
        actions={
          <EditableCardActions
            isEditing={true}
            isSaving={isSaving}
            onEdit={startEditing}
            onSave={save}
            onCancel={cancelEditing}
          />
        }
      >
        <CardTitle>Offre V1</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="v1-status">Statut</Label>
            <Select
              value={formData.status || ''}
              onValueChange={(value) => handleChange('status', value as UpdateSchoolData['status'])}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner" />
              </SelectTrigger>
              <SelectContent>
                {options.statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="v1-type">Type</Label>
            <Select
              value={formData.type || ''}
              onValueChange={(value) => handleChange('type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner" />
              </SelectTrigger>
              <SelectContent>
                {options.types.map((t) => (
                  <SelectItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="v1-support">Support</Label>
            <Select
              value={formData.support || ''}
              onValueChange={(value) => handleChange('support', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Selectionner" />
              </SelectTrigger>
              <SelectContent>
                {options.supports.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap gap-6">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="v1-vip"
              checked={formData.vip}
              onCheckedChange={(checked) => handleChange('vip', checked as boolean)}
            />
            <Label htmlFor="v1-vip">Client VIP</Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="v1-maintenance"
              checked={formData.maintenance}
              onCheckedChange={(checked) => handleChange('maintenance', checked as boolean)}
            />
            <Label htmlFor="v1-maintenance">En maintenance</Label>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox
              id="v1-overdueAccount"
              checked={formData.overdueAccount}
              onCheckedChange={(checked) => handleChange('overdueAccount', checked as boolean)}
            />
            <Label htmlFor="v1-overdueAccount">Compte en retard</Label>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="v1-productionDate">Date de production</Label>
            <Input
              id="v1-productionDate"
              type="date"
              value={formData.productionDate || ''}
              onChange={(e) => handleChange('productionDate', e.target.value || null)}
            />
          </div>
          {formData.overdueAccount && (
            <div className="space-y-2">
              <Label htmlFor="v1-overdueDate">Date compte en retard</Label>
              <Input
                id="v1-overdueDate"
                type="date"
                value={formData.overdueAccountDate || ''}
                onChange={(e) => handleChange('overdueAccountDate', e.target.value || null)}
              />
            </div>
          )}
        </div>

        <div className="pt-4 border-t">
          <Label className="text-sm font-medium mb-3 block">
            Modules ({options.modules.filter((m) => !m.isV2 && moduleIds.includes(m.id)).length})
          </Label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {options.modules
              .filter((m) => !m.isV2)
              .map((module) => (
                <div
                  key={module.id}
                  className="flex items-center space-x-2 p-2 rounded border bg-white hover:bg-muted/50"
                >
                  <Checkbox
                    id={`v1-module-${module.id}`}
                    checked={moduleIds.includes(module.id)}
                    onCheckedChange={() => handleModuleToggle(module.id)}
                  />
                  <Label
                    htmlFor={`v1-module-${module.id}`}
                    className="text-sm font-normal cursor-pointer flex-1"
                  >
                    {module.name}
                  </Label>
                </div>
              ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
